import type React from "react"

import type { AppShellNavigationItem } from "@/app-shell/navigation/AppShellContributionsContext"
import { LockKeyhole, Settings } from "lucide-react"

import { openCoviHome } from "@/lib/coviEvents"
import { filterNavigationItems } from "@/utils/auth/user-access"

import { IMPROVEMENT_LOOP_NAV_ITEMS } from "../config/improvementLoopNavigation"
import {
  HOME_BASE_HREF,
  siteNavigationConfig,
  SOFIA_HOME_NAV_OVERRIDE,
} from "../config/siteNavigation"
import type { NavigationFeatureFlag } from "../types"
import {
  getSidebarItemKey,
  getSidebarSubItemKey,
} from "../utils/sidebarNavKeys"

const DASHBOARD_BASE_HREF = "/dashboard"
const SETTINGS_KEY = "settings"
const SETTINGS_TITLE = "Settings"
const SETTINGS_BASE_HREF = "/settings"
const ADMIN_KEY = "admin"
const ADMIN_TITLE = "Admin"
const ADMIN_BASE_HREF = "/admin"
const ADMIN_HREF = "/admin/organizations"
const HUMAN_REVIEW_LEGACY_BASE_HREF = "/review/projects"
const HUMAN_REVIEW_ENTRY_BASE_HREF = "/review"

interface NavigationFilterItem extends AppShellNavigationItem {
  [key: string]: unknown
  subItems?: NavigationFilterItem[]
}

/**
 * Sofia answers the event when she is mounted, and only then is the link's own
 * navigation suppressed; without a provider the plain `/home` href still works.
 * Modified and non-primary clicks are left alone so "open in new tab" behaves.
 */
function resumeSofiaOnHome(event: React.MouseEvent<HTMLAnchorElement>): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return
  }
  if (openCoviHome()) {
    event.preventDefault()
  }
}

interface BuildAppShellNavigationItemsOptions {
  featureFlagStatus: Partial<Record<NavigationFeatureFlag, boolean>>
  getHref: (baseHref: string) => string
  hasAdminAccess: boolean
  /** Swaps the flat nav for the loop-grouped one. */
  improvementLoopNavEnabled?: boolean
  userEmail: string
}

export function buildAppShellNavigationItems({
  featureFlagStatus,
  getHref,
  hasAdminAccess,
  improvementLoopNavEnabled = false,
  userEmail,
}: BuildAppShellNavigationItemsOptions): AppShellNavigationItem[] {
  const getFeatureAwareBaseHref = (baseHref: string): string =>
    baseHref === HUMAN_REVIEW_LEGACY_BASE_HREF &&
    featureFlagStatus["human-review-role-modes"]
      ? HUMAN_REVIEW_ENTRY_BASE_HREF
      : baseHref
  const navItems = improvementLoopNavEnabled
    ? IMPROVEMENT_LOOP_NAV_ITEMS
    : siteNavigationConfig.navItems
  const items: NavigationFilterItem[] = navItems
    .filter((item) => {
      if (!item.featureFlag) {
        return true
      }

      return featureFlagStatus[item.featureFlag] === true
    })
    .map((item): NavigationFilterItem => {
      const subItems = item.subItems
        ?.filter((subItem) => {
          if (!subItem.featureFlag) {
            return true
          }

          return featureFlagStatus[subItem.featureFlag] === true
        })
        .map((subItem) => {
          const baseHref = getFeatureAwareBaseHref(subItem.base_href)

          return {
            key: getSidebarSubItemKey(subItem),
            href: getHref(baseHref),
            base_href: baseHref,
            createHref: subItem.createBaseHref
              ? getHref(subItem.createBaseHref)
              : undefined,
            createLabel: subItem.createLabel,
            title: subItem.label,
            icon: subItem.icon,
          }
        })

      // Home is a Sofia surface: it wears the Sofia name and the Coval mark.
      const isSofiaHome = item.base_href === HOME_BASE_HREF
      const baseHref = getFeatureAwareBaseHref(item.base_href)
      const sidebarItem: NavigationFilterItem = {
        key: getSidebarItemKey(item),
        href: getHref(baseHref),
        base_href: baseHref,
        createHref: item.createBaseHref
          ? getHref(item.createBaseHref)
          : undefined,
        createLabel: item.createLabel,
        icon: isSofiaHome ? SOFIA_HOME_NAV_OVERRIDE.icon : item.icon,
        title: isSofiaHome ? SOFIA_HOME_NAV_OVERRIDE.label : item.label,
        onClick: isSofiaHome ? resumeSofiaOnHome : undefined,
        // Sofia's own pages hang under this entry as their own sidebar rows,
        // so it is selected on Home itself rather than on the whole section.
        matchExact: isSofiaHome || undefined,
        subItems: subItems && subItems.length > 0 ? subItems : undefined,
      }

      // Dashboard lists and actions are dashboard-owned, not static config.
      // The loop nav points its Explore section at the same route, and those
      // children are static config, so the strip applies to the flat nav only.
      if (
        !improvementLoopNavEnabled &&
        item.base_href === DASHBOARD_BASE_HREF
      ) {
        sidebarItem.subItems = undefined
      }

      return sidebarItem
    })

  items.push({
    key: SETTINGS_KEY,
    href: getHref(SETTINGS_BASE_HREF),
    base_href: SETTINGS_BASE_HREF,
    title: SETTINGS_TITLE,
    icon: Settings,
  })

  if (hasAdminAccess) {
    items.push({
      key: ADMIN_KEY,
      href: ADMIN_HREF,
      base_href: ADMIN_BASE_HREF,
      title: ADMIN_TITLE,
      icon: LockKeyhole,
    })
  }

  return filterNavigationItems(items, userEmail)
}
